import React from 'react'
import useSWRImmutable from 'swr/immutable'
import useSWRMutation from 'swr/mutation'

import { useI18N } from '../../../libraries/i18n/hook'
import RadioButton from '../../components/form/RadioButton'
import useForm from '../../../libraries/form/hook'
import { updateUserPreferences } from '../../../api/users'
import ErrorMessage from '../../components/errors/ErrorMessage'
import FormGroup from '../../components/form/FormGroup'
import { simpleFetch } from '../../../api'
import Loading from '../../components/layout/Loading'
import type { Category } from '../../../api/categories/types'
import { UserGoals, type UserPreferences } from '../../../api/users/types'
import Checkbox from '../../components/form/Checkbox'
import FloatingSubmitButton from './FloatingSubmitButton'

type Props = { onSuccess?: () => void,defaultValues?: Partial<UserPreferences> }
const PreferencesForm: React.FC<Props> = ({ onSuccess, defaultValues }) => {
  const { t } = useI18N()
  const { data: categories, isLoading } = useSWRImmutable('/categories', simpleFetch<Category>)

  const { fields, error, submitting, handleChange, handleSubmit } = useForm<Partial<UserPreferences>, UserPreferences, '/users/preferences'>({
    initialValues: defaultValues ?? { favouriteCategories: [], goals: [] },
    mutation: useSWRMutation('/users/preferences', updateUserPreferences, { populateCache: true }),
    onSuccess,
  })

  const renderGroupCheckbox = (name: keyof UserPreferences, options: Array<{ value: string, label: string }>) => (
    <div className='inline-flex flex-wrap gap-4 justify-center items-center'>
      {options?.map(option => {
        const checked = (fields[name] as string[]).map(item => item.toString())?.includes(option.value.toString())
        return (
          <Checkbox
            name={name}
            id={`${name}-${option.value}`}
            label={option.label}
            onChange={handleChange}
            key={`${name}-${option.value}`}
            value={option.value.toString()}
            checked={checked}
            required={(fields[name] as number[])?.length === 0}
          />
        )
      })}
    </div>
  )

  const renderGroupRadioButtons = (name: keyof UserPreferences, values: string[], leftBottomLabel: string, rightBotomLabel: string) => {
    return (
      <div className='mx-auto'>
        <div className='inline-flex gap-x-2 sm:gap-x-4 justify-center'>
          {values.map(value => (
            <div key={`${name}-${value}`} className='w-12 h-12'>
              <RadioButton
                onChange={handleChange} name={name} value={value}
                checked={fields[name]?.toString() === value}
                label={value}
                required={fields[name] === undefined}
              />
            </div>
          ))}
        </div>
        <div className='flex justify-between mt-2'>
          <p className='text-xs font-light'>{leftBottomLabel}</p>
          <p className='text-xs font-light'>{rightBotomLabel}</p>
        </div>
      </div>
    )
  }

  if(isLoading) {
    return <Loading />
  }

  return (
    <form onSubmit={handleSubmit}>
      <FormGroup label={t('onboarding.preferences.knowing.profOps')}>
        {renderGroupRadioButtons('knowingProffesionalOportunities', ['1', '2', '3', '4', '5'], t('common.nothing'), t('common.lots'))}
      </FormGroup>
      <FormGroup label={t('onboarding.preferences.knowing.goals')}>
        {renderGroupRadioButtons('knowingCareerGoal', ['1', '2', '3', '4', '5'], t('common.nothing.clear'), t('common.all.clear'))}
      </FormGroup>
      <FormGroup label={t('onboarding.preferences.which.fav.topics')}>
        {renderGroupCheckbox('favouriteCategories', categories?.map(category => ({ value: category.id, label: category.name })) ?? [])}
      </FormGroup>
      <div className='pb-6 sm:pb-0'>
        <FormGroup label={t('onboarding.preferences.which.goals')}>
          {renderGroupCheckbox(
            'goals', [
              { value: UserGoals.EXPLORE_JOB_OFFERS, label: t('onboarding.preferences.goals.explore.jobs') },
              { value: UserGoals.GET_JOB_OFFERS, label: t('onboarding.preferences.goals.get.jobs') },
              { value: UserGoals.GET_READY_FOR_JOB_OFFERS, label: t('onboarding.preferences.goals.prepare.interviews') },
            ]
          )}
        </FormGroup>
      </div>

      <FloatingSubmitButton disabled={submitting}>{t('common.continue')}</FloatingSubmitButton>
      {error && <ErrorMessage error={error} />}
    </form>
  )
}

export default PreferencesForm
